import type { MasterOrderDetailsFragment } from "~/gql/queries/masterOrder.generated";
import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { MasterOrderInputs, MasterOrderSavedChanges } from "~/store/masterOrderSlice";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";

import isEqual from "lodash/isEqual";

import {
	AddMasterLineItemDataInput,
	AddressData,
	AddressDataInput,
	EditMasterOrderInput,
	MasterOrderContactData,
	MasterOrderContactDataInput,
	MasterOrderCustomerDataInput,
	ShipFromLocationData,
	ShipFromLocationDataInput
} from "~/gql/graphqlBase.generated";
import isRecord from "~/helpers/isRecord";

const MASTER_ORDER_SOURCE_KIND = "masterOrder";

export interface MasterOrderSourceCheckpoint {
	acknowledgedQuantities: Record<string, number>;
	addItem?: AddMasterLineItemDataInput;
	masterOrderCustomer: MasterOrderCustomerDataInput | null;
	masterOrderId: string;
	masterOrderInputs: MasterOrderInputs;
	masterOrderNumber: string;
	savedMaster?: Pick<MasterOrderDetailsFragment, "poNumber" | "type" | "salesPerson" | "status"> & {
		customer: MasterOrderCustomerDataInput;
	};
}

const isQuantities = (value: unknown): value is Record<string, number> =>
	isRecord(value) && Object.values(value).every((quantity) => typeof quantity === "number");

const isMasterOrderSourceCheckpoint = (value: unknown): value is MasterOrderSourceCheckpoint =>
	isRecord(value) &&
	typeof value.masterOrderId === "string" &&
	typeof value.masterOrderNumber === "string" &&
	isRecord(value.masterOrderInputs) &&
	(value.masterOrderCustomer === null || isRecord(value.masterOrderCustomer)) &&
	isQuantities(value.acknowledgedQuantities) &&
	(value.addItem === undefined || isRecord(value.addItem)) &&
	(value.savedMaster === undefined ||
		(isRecord(value.savedMaster) &&
			typeof value.savedMaster.poNumber === "string" &&
			typeof value.savedMaster.salesPerson === "string" &&
			typeof value.savedMaster.type === "string" &&
			typeof value.savedMaster.status === "string" &&
			isRecord(value.savedMaster.customer) &&
			isRecord(value.savedMaster.customer.contact) &&
			isRecord(value.savedMaster.customer.location)));

const reviewSavedHeader = (
	checkpoint: MasterOrderSourceCheckpoint,
	saved: NonNullable<MasterOrderSourceCheckpoint["savedMaster"]>
) => {
	const inputs = { ...checkpoint.masterOrderInputs };
	let customer = checkpoint.masterOrderCustomer;
	const changedFields: MasterOrderSavedChanges = {};
	const baseline = checkpoint.savedMaster;
	if (!baseline) return { changedFields, customer, inputs };
	if (baseline.poNumber !== saved.poNumber) {
		changedFields.poNumber = inputs.poNumber;
		inputs.poNumber = saved.poNumber;
	}
	if (baseline.type !== saved.type) {
		changedFields.type = inputs.type;
		inputs.type = saved.type;
	}
	if (baseline.salesPerson !== saved.salesPerson) {
		changedFields.salesPersonEmail = inputs.salesPersonEmail;
		inputs.salesPersonEmail = saved.salesPerson;
	}
	const currentCustomer: MasterOrderCustomerDataInput = {
		billToAddress: saved.customer.billToAddress,
		contact: saved.customer.contact,
		id: saved.customer.id,
		location: saved.customer.location,
		name: saved.customer.name
	};
	if (
		baseline.customer.id !== saved.customer.id ||
		baseline.customer.name !== saved.customer.name ||
		!isEqual(baseline.customer.location, saved.customer.location)
	) {
		changedFields.location = customer ? `${customer.name} / ${customer.location.name}` : "";
		customer = {
			...(customer ?? currentCustomer),
			id: saved.customer.id,
			name: saved.customer.name,
			location: saved.customer.location
		};
	}
	if (!isEqual(baseline.customer.billToAddress ?? null, saved.customer.billToAddress ?? null)) {
		const address = customer?.billToAddress;
		changedFields.billToAddress = address
			? [address.toName, address.addressLine1, address.city, address.stateProvince, address.zipcode]
					.filter(Boolean)
					.join(", ")
			: "";
		customer = { ...(customer ?? currentCustomer), billToAddress: saved.customer.billToAddress };
	}
	for (const field of ["firstName", "lastName", "emailAddress", "phoneNumber"] as const) {
		if (isEqual(baseline.customer.contact[field], saved.customer.contact[field])) continue;
		const previous = customer?.contact[field];
		changedFields[field] = typeof previous === "string" ? previous : (previous?.value ?? "");
		customer = {
			...(customer ?? currentCustomer),
			contact: { ...(customer ?? currentCustomer).contact, [field]: saved.customer.contact[field] }
		};
	}
	if (baseline.status !== saved.status) changedFields.status = baseline.status;
	return { changedFields, customer, inputs };
};

const hasHeaderChanges = (
	inputs: Pick<MasterOrderInputs, "poNumber" | "type">,
	customer: MasterOrderCustomerDataInput,
	saved: Pick<MasterOrderDetailsFragment, "poNumber" | "type"> & {
		customer: Pick<MasterOrderCustomerDataInput, "id" | "contact" | "billToAddress"> & {
			location: Pick<MasterOrderCustomerDataInput["location"], "id">;
		};
	}
): boolean =>
	inputs.poNumber !== saved.poNumber ||
	inputs.type !== saved.type ||
	customer.id !== saved.customer.id ||
	customer.location.id !== saved.customer.location.id ||
	!isEqual(customer.contact, saved.customer.contact) ||
	!isEqual(customer.billToAddress, saved.customer.billToAddress);

const masterOrderEditorKey = (masterOrderId: string): string => `masterOrder:${masterOrderId}`;

const masterOrderContinuation = {
	hasHeaderChanges,
	isMasterOrderSourceCheckpoint,
	reviewSavedHeader,
	MASTER_ORDER_SOURCE_KIND,
	masterOrderEditorKey
} as const;

export default masterOrderContinuation;

export const MASTER_ORDER_TARGET_KIND = "masterOrder";

export const buildMasterOrderPath = (id: string): string => `/fulfill/orders/master-order/${encodeURIComponent(id)}`;

const masterOrderReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: ({ targetId }) => buildMasterOrderPath(targetId),
	matchesRoute: (pathname): ContinuationTarget | null => {
		const match = /^\/fulfill\/orders\/master-order\/([^/?#]+)$/.exec(pathname);
		if (!match) return null;
		try {
			return { targetId: decodeURIComponent(match[1]), targetKind: MASTER_ORDER_TARGET_KIND };
		} catch {
			return null;
		}
	}
};

export interface MasterOrderHeaderAcknowledgment {
	baseline: MasterOrderDetailsFragment;
	input?: EditMasterOrderInput;
	salesPersonEmail?: string;
}

const normalizeAddress = (address: AddressData | AddressDataInput | null | undefined) =>
	address
		? {
				addressLine1: address.addressLine1,
				addressLine2: address.addressLine2,
				city: address.city,
				country: address.country,
				id: address.id,
				stateProvince: address.stateProvince,
				toName: address.toName,
				zipcode: address.zipcode
			}
		: null;

const normalizeContact = (contact: MasterOrderContactData | MasterOrderContactDataInput | null | undefined) =>
	contact
		? {
				emailAddress: { type: contact.emailAddress.type, value: contact.emailAddress.value },
				firstName: contact.firstName,
				lastName: contact.lastName,
				phoneNumber: { type: contact.phoneNumber.type, value: contact.phoneNumber.value },
				title: contact.title,
				type: contact.type
			}
		: null;

const normalizeShipFromLocation = (location: ShipFromLocationData | ShipFromLocationDataInput | null | undefined) =>
	location ? { name: location.name } : null;

export const matchesAcknowledgedMasterOrderHeader = (
	saved: MasterOrderDetailsFragment,
	acknowledgment: MasterOrderHeaderAcknowledgment
): boolean => {
	const { baseline, input, salesPersonEmail } = acknowledgment;
	const expectedCustomer = input ? input.customer : baseline.customer;
	const expectedSalesPerson = salesPersonEmail !== undefined ? salesPersonEmail : (baseline.salesPerson ?? "");
	const savedShipFrom = normalizeShipFromLocation(saved.shipFromLocation);
	const shipFromMatches =
		isEqual(savedShipFrom, normalizeShipFromLocation(baseline.shipFromLocation)) ||
		(Boolean(input) && isEqual(savedShipFrom, normalizeShipFromLocation(input?.fromLocation)));

	return (
		saved.masterOrderId === (input ? input.masterOrderId : baseline.masterOrderId) &&
		saved.masterOrderNumber === (input ? input.masterOrderNumber : baseline.masterOrderNumber) &&
		saved.poNumber === (input ? input.poNumber : baseline.poNumber) &&
		saved.type === (input ? input.type : baseline.type) &&
		(saved.salesPerson ?? "") === expectedSalesPerson &&
		saved.status === baseline.status &&
		saved.tenantId === baseline.tenantId &&
		saved.customer.id === expectedCustomer.id &&
		saved.customer.name === expectedCustomer.name &&
		(saved.customer.location?.id ?? null) === (expectedCustomer.location?.id ?? null) &&
		isEqual(normalizeAddress(saved.customer.billToAddress), normalizeAddress(expectedCustomer.billToAddress)) &&
		isEqual(normalizeContact(saved.customer.contact), normalizeContact(expectedCustomer.contact)) &&
		isEqual(saved.masterLineItems, baseline.masterLineItems) &&
		isEqual(saved.scheduledOrders ?? null, baseline.scheduledOrders ?? null) &&
		isEqual(saved.confirmationEmails ?? null, baseline.confirmationEmails ?? null) &&
		shipFromMatches
	);
};

export { masterOrderReceiverIdentityAdapter };
